import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../services/api";
import PageHero from "../components/PageHero/PageHero";
import ApoieButton from "../components/ApoieButton/ApoieButton";
import { SuccessDialog } from "../components/Admin/Dialogs";
import FormActions from "../components/Admin/FormActions/FormActions";
import { UploadImageIcon } from "../assets/UploadImageIcon";
import { useAdminPage } from "../hooks/useAdminPage";
import { getImageUrl, getVideoEmbedUrl } from "../utils/media";
import apoieHeroImage from "../assets/images/apoie-hero-shaped.png";
import apoieSlideImage from "../assets/images/apoie-slide.png";
import "./Apoie.css";

const PLACEHOLDER_TEXT = "";

const DEFAULT_MODALITIES = [
  "Seja um padrinho!",
  "Faça uma doação única!",
  "Doação de produtos e serviços",
  "Destine parte do seu imposto de renda!",
].map((title, order) => ({ title, text: PLACEHOLDER_TEXT, order }));

const APOIE_VIDEO_URL = "https://www.youtube.com/watch?v=bhs7bj4BgSk";
const DEFAULT_DONATION_URL = "https://7geracoes.apoiar.co";
const APOIE_SYNC_EVENT = "7geracoes:apoie-atualizada";
const APOIE_SYNC_STORAGE_KEY = "7geracoes:apoie-atualizada";

const DEFAULT_PAGE = {
  introduction: {
    title: "Apoie",
    text: PLACEHOLDER_TEXT,
    slideshow: [{ url: apoieSlideImage, order: 0, fallback: true }],
  },
  modalities: {
    title: "Formas de apoiar",
    items: DEFAULT_MODALITIES,
  },
  donation: {
    video: {
      url: APOIE_VIDEO_URL,
      platform: "youtube",
      embedUrl: getVideoEmbedUrl(APOIE_VIDEO_URL) || "",
    },
    link: DEFAULT_DONATION_URL,
  },
};

function normalizePage(data) {
  const source = data || {};
  const introduction = source.introduction || {};
  const modalities = source.modalities || {};
  const sourceItems = Array.isArray(modalities.items) ? modalities.items : [];
  const donation = source.donation || {};
  const sourceSlides = Array.isArray(introduction.slideshow)
    ? introduction.slideshow.filter((slide) => getImageUrl(slide))
    : [];

  return {
    ...source,
    introduction: {
      ...DEFAULT_PAGE.introduction,
      ...introduction,
      title: introduction.title || DEFAULT_PAGE.introduction.title,
      text: introduction.text ?? DEFAULT_PAGE.introduction.text,
      slideshow: sourceSlides.length
        ? sourceSlides.map((slide, order) => ({ ...slide, order }))
        : DEFAULT_PAGE.introduction.slideshow.map((slide) => ({ ...slide })),
    },
    modalities: {
      ...DEFAULT_PAGE.modalities,
      ...modalities,
      items: DEFAULT_MODALITIES.map((fallback, index) => ({
        ...fallback,
        ...(sourceItems[index] || {}),
        title: sourceItems[index]?.title || fallback.title,
        text: sourceItems[index]?.text ?? fallback.text,
        order: index,
      })),
    },
    donation: {
      ...DEFAULT_PAGE.donation,
      ...donation,
      link: donation.link?.trim() || DEFAULT_PAGE.donation.link,
      video: {
        ...DEFAULT_PAGE.donation.video,
        // Só cai no vídeo padrão quando ainda não existe nada salvo;
        // string vazia é um vídeo removido intencionalmente pelo admin.
        url: donation.video?.url ?? DEFAULT_PAGE.donation.video.url,
        platform: donation.video?.platform || DEFAULT_PAGE.donation.video.platform,
      },
    },
  };
}

function clonePage(page) {
  return {
    ...page,
    introduction: {
      ...page.introduction,
      slideshow: page.introduction.slideshow.map((slide) => ({ ...slide })),
    },
    modalities: {
      ...page.modalities,
      items: page.modalities.items.map((item) => ({ ...item })),
    },
    donation: {
      ...page.donation,
      video: { ...page.donation.video },
    },
  };
}

function ContentBlock({ title, text, editing, onTitleChange, onTextChange }) {
  if (!editing) {
    return (
      <section className="apoie-content-block">
        <h2>{title}</h2>
        <p>{text}</p>
      </section>
    );
  }

  return (
    <section className="apoie-content-block apoie-content-block--editing">
      <label className="apoie-content-block__title-field">
        <input
          className="apoie-content-block__title-input"
          value={title}
          size={Math.max(String(title || "").length, 1)}
          aria-label="Título"
          onChange={(event) => onTitleChange(event.target.value)}
        />
      </label>
      <label className="apoie-content-block__text-field">
        <textarea
          className="apoie-content-block__text-input"
          value={text}
          aria-label="Texto"
          onChange={(event) => onTextChange(event.target.value)}
        />
      </label>
    </section>
  );
}

function Carousel({ slides, current, onPrevious, onNext, editing, onUpload, onRemove, canRemove }) {
  const inputRef = useRef(null);
  const slide = slides[current] || slides[0];

  return (
    <section className={`apoie-carousel ${editing ? "apoie-carousel--editing" : ""}`}>
      <button
        type="button"
        className="apoie-carousel__arrow apoie-carousel__arrow--previous"
        aria-label="Imagem anterior"
        onClick={onPrevious}
      >
        <span aria-hidden="true" />
      </button>

      <div className="apoie-carousel__media">
        <img src={getImageUrl(slide) || apoieSlideImage} alt="Atividades do Instituto 7 Gerações" />
      </div>

      <button
        type="button"
        className="apoie-carousel__arrow apoie-carousel__arrow--next"
        aria-label="Próxima imagem"
        onClick={onNext}
      >
        <span aria-hidden="true" />
      </button>

      {editing && (
        <div className="apoie-carousel__upload">
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png"
            multiple
            onChange={onUpload}
          />
          <button
            type="button"
            className="apoie-carousel__add"
            aria-label="Adicionar imagem"
            title="Adicionar imagem"
            onClick={() => inputRef.current?.click()}
          >
            <UploadImageIcon />
            <span>Adicionar imagem</span>
          </button>
          <small>JPG ou PNG, até 15 MB</small>
          <button
            type="button"
            className="apoie-carousel__remove"
            onClick={onRemove}
            disabled={!canRemove}
          >
            Remover imagem atual
          </button>
        </div>
      )}
    </section>
  );
}

export default function Apoie() {
  const adminPage = useAdminPage();
  const [page, setPage] = useState(() => normalizePage());
  const [snapshot, setSnapshot] = useState(null);
  const [current, setCurrent] = useState(0);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);

  const loadPage = useCallback(async () => {
    try {
      const response = await api.get("/config/apoie", {
        params: { _t: Date.now() },
      });
      setPage(normalizePage(response.data));
    } catch {
      setPage(normalizePage());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  useEffect(() => {
    const syncPage = (event) => setPage(normalizePage(event.detail));
    const syncStorage = (event) => {
      if (event.key !== APOIE_SYNC_STORAGE_KEY || !event.newValue) return;

      try {
        const stored = JSON.parse(event.newValue);
        if (stored.page) syncPage({ detail: stored.page });
      } catch {
        // Ignore invalid synchronization data from another tab.
      }
    };

    window.addEventListener(APOIE_SYNC_EVENT, syncPage);
    window.addEventListener("storage", syncStorage);
    return () => {
      window.removeEventListener(APOIE_SYNC_EVENT, syncPage);
      window.removeEventListener("storage", syncStorage);
    };
  }, []);

  useEffect(() => {
    if (current >= page.introduction.slideshow.length) setCurrent(0);
  }, [current, page.introduction.slideshow.length]);

  const updateIntroduction = (field, value) => {
    setPage((currentPage) => ({
      ...currentPage,
      introduction: { ...currentPage.introduction, [field]: value },
    }));
  };

  const updateModality = (index, field, value) => {
    setPage((currentPage) => ({
      ...currentPage,
      modalities: {
        ...currentPage.modalities,
        items: currentPage.modalities.items.map((item, itemIndex) =>
          itemIndex === index ? { ...item, [field]: value } : item,
        ),
      },
    }));
  };

  const handleUpload = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (files.length === 0) return;

    const invalidFile = files.find((file) => !["image/jpeg", "image/png"].includes(file.type));
    if (invalidFile) {
      setError("Selecione uma imagem JPG ou PNG.");
      return;
    }

    const oversizedFile = files.find((file) => file.size > 15 * 1024 * 1024);
    if (oversizedFile) {
      setError("A imagem deve ter no máximo 15 MB.");
      return;
    }

    setError("");
    setPage((currentPage) => ({
      ...currentPage,
      introduction: {
        ...currentPage.introduction,
        slideshow: [
          ...currentPage.introduction.slideshow,
          ...files.map((file, fileIndex) => ({
            url: URL.createObjectURL(file),
            newFile: file,
            fallback: false,
            order: currentPage.introduction.slideshow.length + fileIndex,
          })),
        ],
      },
    }));
    setCurrent(page.introduction.slideshow.length);
  };

  const handleRemoveSlide = () => {
    if (page.introduction.slideshow.length === 0) {
      setError("Nenhuma imagem para remover.");
      return;
    }

    setPage((currentPage) => {
      const updatedSlideshow = currentPage.introduction.slideshow
        .filter((_, index) => index !== current)
        .map((slide, index) => ({ ...slide, order: index }));

      return {
        ...currentPage,
        introduction: { ...currentPage.introduction, slideshow: updatedSlideshow },
      };
    });
    setCurrent((index) => Math.max(0, index - 1));
  };

  const beginEditing = () => {
    setSnapshot(clonePage(page));
    setError("");
    setEditing(true);
  };

  const cancelEditing = () => {
    if (snapshot) setPage(snapshot);
    setError("");
    setEditing(false);
  };

  const savePage = async () => {
    const requiredValues = [
      page.introduction.title,
      page.donation.link,
      ...page.modalities.items.map((item) => item.title),
    ];
    if (requiredValues.some((value) => !value?.trim())) {
      setError("Preencha todos os títulos e o link de doação antes de salvar.");
      return;
    }

    if (page.donation.video.url?.trim() && !getVideoEmbedUrl(page.donation.video.url)) {
      setError("Use um link válido de vídeo do YouTube ou Instagram.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const uploadedSlides = [];
      for (const [index, slide] of page.introduction.slideshow.entries()) {
        if (!slide.newFile) {
          if (slide.fallback) continue;

          uploadedSlides.push({
            url: getImageUrl(slide),
            publicId: slide.publicId || slide.public_id || "",
            order: index,
          });
          continue;
        }

        const formData = new FormData();
        formData.append("image", slide.newFile);
        const response = await api.post("/media/upload", formData);
        uploadedSlides.push({
          url: response.data.url,
          publicId: response.data.public_id || response.data.publicId || "",
          order: index,
        });
      }

      const payload = {
        introduction: {
          title: page.introduction.title.trim(),
          text: page.introduction.text.trim(),
          slideshow: uploadedSlides,
        },
        modalities: {
          items: page.modalities.items.map((item, order) => ({
            title: item.title.trim(),
            text: item.text.trim(),
            order,
          })),
        },
        donation: {
          video: {
            url: page.donation.video.url?.trim() || "",
            platform: page.donation.video.platform || "youtube",
            embedUrl: getVideoEmbedUrl(page.donation.video.url?.trim()) || "",
          },
          link: page.donation.link?.trim() || "",
        },
      };

      const response = await api.put("/config/apoie", payload);
      const savedPage = normalizePage(response.data || payload);
      setPage(savedPage);
      setSnapshot(clonePage(savedPage));
      setEditing(false);
      setSuccessOpen(true);
      window.dispatchEvent(
        new CustomEvent(APOIE_SYNC_EVENT, { detail: savedPage }),
      );
      try {
        window.localStorage.setItem(
          APOIE_SYNC_STORAGE_KEY,
          JSON.stringify({ page: savedPage, updatedAt: Date.now() }),
        );
      } catch {
        // The current page is already updated even if storage is unavailable.
      }
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          requestError.response?.data?.message ||
          "Não foi possível salvar as alterações.",
      );
    } finally {
      setSaving(false);
    }
  };

  const slides = page.introduction.slideshow;
  const previousSlide = () =>
    setCurrent((index) => (index === 0 ? slides.length - 1 : index - 1));
  const nextSlide = () =>
    setCurrent((index) => (index === slides.length - 1 ? 0 : index + 1));
  const videoEmbed = getVideoEmbedUrl(page.donation.video.url);

  return (
    <main className={`apoie-page ${adminPage ? "apoie-page--admin" : ""}`}>
      {!adminPage && (
        <PageHero
          className="page-hero--apoie"
          imageUrl={apoieHeroImage}
          imageShape="provided"
          title="Apoie"
        />
      )}

      {adminPage && (
        <header className="apoie-admin-header">
          <h1>EDITAR SEÇÃO</h1>
          <p>Apoie</p>
        </header>
      )}

      {!adminPage && <ApoieButton />}

      <div className="apoie-page__content">
        <ContentBlock
          title={page.introduction.title}
          text={page.introduction.text}
          editing={adminPage && editing}
          onTitleChange={(value) => updateIntroduction("title", value)}
          onTextChange={(value) => updateIntroduction("text", value)}
        />

        <Carousel
          slides={slides}
          current={current}
          onPrevious={previousSlide}
          onNext={nextSlide}
          editing={adminPage && editing}
          onUpload={handleUpload}
          onRemove={handleRemoveSlide}
          canRemove={slides.length > 0}
        />

        <div className="apoie-modalities">
          {page.modalities.items.map((item, index) => (
            <ContentBlock
              key={`${item.order}-${index}`}
              title={item.title}
              text={item.text}
              editing={adminPage && editing}
              onTitleChange={(value) => updateModality(index, "title", value)}
              onTextChange={(value) => updateModality(index, "text", value)}
            />
          ))}
        </div>

        <section className="apoie-donation">
          <div className="apoie-video-card">
            {videoEmbed ? (
              <iframe
                src={videoEmbed}
                title="Vídeo sobre como apoiar o Instituto 7 Gerações"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            ) : (
              <div className="apoie-video-placeholder">
                <img
                  src={apoieSlideImage}
                  alt=""
                  aria-hidden="true"
                />
                <span aria-hidden="true" />
                <p>Vídeo indisponível</p>
              </div>
            )}
          </div>

          {adminPage && editing && (
            <div className="apoie-donation__fields">
              <label>
                <span>Link do vídeo (YouTube ou Instagram)</span>
                <input
                  type="url"
                  value={page.donation.video.url || ""}
                  onChange={(event) =>
                    setPage((currentPage) => ({
                      ...currentPage,
                      donation: {
                        ...currentPage.donation,
                        video: { ...currentPage.donation.video, url: event.target.value },
                      },
                    }))
                  }
                  placeholder="https://www.youtube.com/watch?v=..."
                />
              </label>
              {page.donation.video.url && (
                <button
                  type="button"
                  className="apoie-donation__remove-video"
                  onClick={() =>
                    setPage((currentPage) => ({
                      ...currentPage,
                      donation: {
                        ...currentPage.donation,
                        video: { ...currentPage.donation.video, url: "" },
                      },
                    }))
                  }
                >
                  Remover vídeo
                </button>
              )}
              <label>
                <span>Link para doação</span>
                <input
                  type="url"
                  value={page.donation.link || ""}
                  onChange={(event) =>
                    setPage((currentPage) => ({
                      ...currentPage,
                      donation: { ...currentPage.donation, link: event.target.value },
                    }))
                  }
                  placeholder="https://"
                />
              </label>
            </div>
          )}

          <a
            className="apoie-donation__button"
            href={page.donation.link}
            target="_blank"
            rel="noopener noreferrer"
          >
            Faça sua doação
          </a>
        </section>

        {adminPage && (
          <>
            {error && <span className="error-message">{error}</span>}
            <FormActions
              editing={editing}
              className={editing ? "" : "apoie-form-actions--view"}
              onEdit={beginEditing}
              onCancel={cancelEditing}
              onSave={savePage}
              saving={saving}
              disabled={loading && !editing}
            />
          </>
        )}
      </div>

      <SuccessDialog
        open={successOpen}
        message="Alterações salvas com sucesso!"
        onClose={() => setSuccessOpen(false)}
      />
    </main>
  );
}
